//
//

import type moment from 'moment';
import { PartialRecord } from '../types/utilityTypes';
import { TForcePathsFromRoutes } from './ads';
import { TActivityDataProvider } from './dataProviders';
import { IGameEventConfig } from './events';
import { Expansion, ExpansionLevelCap, ExpansionPatch, TExpansionInfo } from './expansion';
import { TSeasonDef } from './mythicPlus';

//
export enum WoWDifficultyId {
  NORMAL_10 = 3,
  NORMAL_25 = 4,
  HEROIC_10 = 5,
  HEROIC_25 = 6,
  MYTHIC_KEYSTONE = 8,
  NORMAL_RAID = 14,
  HEROIC_RAID = 15,
  MYTHIC_RAID = 16,
  MYTHIC_FLEXIBLE_RAID = 233
}

export enum RaidDifficulty {
  NORMAL_10 = 'normal10',
  NORMAL_25 = 'normal25',
  HEROIC_10 = 'heroic10',
  HEROIC_25 = 'heroic25',
  NORMAL = 'normal',
  HEROIC = 'heroic',
  MYTHIC = 'mythic'
}

export enum CoreRaidDifficulty {
  NORMAL = 'normal',
  HEROIC = 'heroic',
  MYTHIC = 'mythic'
}

export type Difficulty = {
  id: WoWDifficultyId;
  slug: RaidDifficulty;
  name: string;
  acronym: string;
  maxMembers: number;
  wowDifficultyIds?: WoWDifficultyId[];
  approximateDifficulty?: RaidDifficulty; // when raid tier are approximate, use this difficulty in characterProgress
};

export enum RealmConnectionType {
  LIVE = 'live',
  PTR = 'ptr',
  BETA = 'beta',
  TR = 'tr'
}

export enum RealmTypeFlags {
  NORMAL = 1,
  PVP = 2,
  RP = 4,
  HARDCORE = 8,
  TOURNAMENT = 16,
  SEASON_OF_DISCOVERY = 32,
  ANNIVERSARY_REALM = 64
}

// type OVERRIDE_TYPE = any;

export type TRegionalTimes = {
  us: moment.Moment;
  eu: moment.Moment;
  kr: moment.Moment;
  tw: moment.Moment;
  cn: moment.Moment;
  [key: string]: moment.Moment;
};

export type TFatedRaidInterval = {
  weeks: number; // how many weeks between
  weeksActive: number; // when active, how long does it last
  rotationStartsAt: TRegionalTimes;
  rotationEndsAt: TRegionalTimes;
  alwaysActiveAfterRotationEnds: boolean;
  alwaysActiveEndsAt: TRegionalTimes;
};

export type TRaidKillCountsByDifficulty = PartialRecord<RaidDifficulty | string, number>;

export interface ITimeBoundsMetadata {
  starts: TRegionalTimes;
  ends: TRegionalTimes;
}

export interface TRaidPartitionMetadata {
  name: string;
  slug: string;
  bossEncounterIds: number[];
  opensAt?: TRegionalTimes;
}

export interface TBaseRaidMetadata extends ITimeBoundsMetadata {
  name: string;
  short_name: string;
  slug: string;
  icon?: string;
  mapId: number;
  tier: string; // this needs to be a string rather than a number to cover for things like Fated seasons
  numEncounters: number;
  ordinal: number;
  expansion: Expansion;
  patch: ExpansionPatch;
  supportsFactions?: boolean;
  supportsWorldFirstTracker?: boolean;
  supportsLogging?: boolean;
  supportsGlobalCoverage?: boolean;
  supportsGraphs?: boolean;
  supportsTalentTrees?: boolean;
  fatedInterval?: TFatedRaidInterval;
  globalCoverageSlug?: string;
  globalCoverageTitle?: string;
  leaderboardByBestPull?: boolean;
  disabledRegions?: string[];
  hideCharacterBossKills?: boolean; // for fated raids we do not want to show boss kills because we cannot track the exact amount
  dataProviders?: TActivityDataProvider[];
  supportedDifficulties: RaidDifficulty[];
  starts: TRegionalTimes;
  difficultyStarts?: PartialRecord<RaidDifficulty | string, TRegionalTimes>;
  ends: TRegionalTimes;
  // When CE/AOTC are cut off. Should be implied to be as regular "ends", but
  // for things like pre-patch the season may still be playable but things like
  // CE are no longer earnable.
  cutoffsEnds?: TRegionalTimes;
  membersNeededForKill: TRaidKillCountsByDifficulty;
  approximateDifficultySize?: boolean;

  partitions?: TRaidPartitionMetadata[];
}

// all known feature flags
export enum FeatureFlag {
  DO_NOT_USE_PARAMS = 'params', // special value where the params data will go. Never set this.
  AUTHENTICATED = 'authenticated',
  AC_DOCKING = 'ac_docking',
  AC_DOCKING_CHARACTER = 'ac_docking_character',
  AC_DOCKING_GUILD = 'ac_docking_guild',
  AC_QUEUE = 'ac_queue',
  ADMIN = 'admin',
  SUPPORT_TEAM = 'support_team',
  ADMIN_SEARCH = 'admin_search',
  ADMIN_VIEW_CLIENT_USAGE = 'admin_view_client_usage',
  ADMIN_SHOW_USER_PROFILES = 'admin_show_user_profiles',
  ADMIN_VIEW_ALL_LIVE_RAIDS = 'admin_view_all_live_raids',
  API_TOOLS = 'api_tools',
  CHARACTER_COMPARE = 'character_compare',
  CHARACTER_TRANSFERS = 'character_transfers',
  CLIENT_LOCALIZATION = 'client_localization',
  SEGMENT_UPLOAD = 'segment_upload',
  COMBAT_LOGGING = 'combat_logging',
  COMBAT_LOG_DOWNLOAD = 'combat_log_download',
  COMBAT_LOGGING_ALLOW_READ_FROM_START = 'combat_logging_allow_read_from_start',
  COMBAT_LOGGING_FORCE_READ_FROM_START = 'combat_logging_force_read_from_start',
  DEVELOPER_ADMIN = 'developer_admin',
  DOWNLOAD_REPLAYS = 'download_replays',
  REPLAY_PREMIUM = 'replay_premium',
  FEATURE_ADMIN = 'feature_admin',
  KEYSTONE_REPLAY_SETS_MANAGER = 'keystone_replay_sets_manager',
  IMPORT_LOG = 'import_log',
  BLIZZ_TGP_DF2 = 'blizz_tgp_df2',
  DISPLAY_GUILD_CLASH = 'display_guild_clash',
  FORCE_GUILD_USERS = 'force_guild_users',
  SHOW_ALL_CHARACTER_LOADOUTS = 'show_all_character_loadouts',
  NEW_SEASON_RECAP = 'new_season_recap',
  TGP_LIVE = 'tgp_live',

  LIVE_RAID_ALL = 'live_raid-all',
  LIVE_RAID_BETA = 'live_raid-beta',
  LIVE_RAID_LIVE = 'live_raid-live',
  LIVE_RAID_PTR = 'live_raid-ptr',
  LIVE_RAID_TR = 'live_raid-tr',

  LIVE_RUN_ALL = 'live_run-all',
  LIVE_RUN_BETA = 'live_run-beta',
  LIVE_RUN_LIVE = 'live_run-live',
  LIVE_RUN_PTR = 'live_run-ptr',
  LIVE_RUN_TR = 'live_run-tr',

  MDI_MANAGER = 'mdi_manager',
  MDI_TOOLS = 'mdi_tools',
  RWF_HELPER = 'rwf_helper',
  MESSAGE_CENTER = 'message_center',
  NEWS_ADMIN = 'news_admin',
  NEWS_ALERTER = 'news_alerter',
  NEWS_EDITOR = 'news_editor',
  NEWS_PUBLISHER = 'news_publisher',
  NEWS_STICKY = 'news_sticky',
  NO_ADS = 'no_ads',
  FORCE_CLIENT_ADS = 'force_client_ads',
  ALLOW_CLIENT_ADS = 'allow_client_ads',
  ALLOW_CLIENT_PLACEHOLDERS = 'allow_client_placeholders',
  OBSERVER = 'observer',

  PREMIUM = 'premium',
  PREMIUM_1 = 'premium_1',
  PREMIUM_2 = 'premium_2',
  PREMIUM_3 = 'premium_3',
  PREMIUM_4 = 'premium_4',
  PREMIUM_5 = 'premium_5',
  PREMIUM_BGS_1 = 'premium_bgs_1',
  PREMIUM_RECRUITMENT_THREADS = 'premium_recruitment_threads',
  PREMIUM_SCAN_ACCOUNT_ON_BNET_REFRESH = 'premium_scan_account_on_bnet_refresh',
  ADDON_PREVIOUS_SCORE_THRESHOLD = 'addon_previous_score_threshold',
  ADDON_ROLE_VISIBILITY = 'addon_role_visibility',
  PREMIUM_SEARCH = 'premium_search',
  RAID_ENCOUNTER_SEARCH = 'raid_encounter_search',

  QUEUE_PRIORITY_1 = 'queue_priority_1',
  QUEUE_PRIORITY_2 = 'queue_priority_2',
  QUEUE_PRIORITY_3 = 'queue_priority_3',
  QUEUE_PRIORITY_4 = 'queue_priority_4',
  QUEUE_PRIORITY_ADMIN = 'queue_priority_admin',
  TALENT_BUILDS_NAV = 'talent_builds_nav',
  RECRUITMENT = 'recruitment',
  RECRUITMENT_CREATE = 'recruitment_create',
  RECRUITMENT_CREATE_TEAMS = 'recruitment_create_teams',
  RECRUITMENT_SEARCH = 'recruitment_search',
  RECRUITMENT_SEARCH_ADMIN = 'recruitment_search_admin',
  RECRUITMENT_VIEW = 'recruitment_view',
  RWF_MANAGER = 'rwf_manager',
  RWF_LOOT_TRACKING = 'rwf_loot_tracking',
  PUSH_NOTIFICATIONS_ENABLED = 'push_notifications_enabled',
  PUSH_NOTIFICATIONS_UI = 'push_notifications_ui',
  RWF_HOMEPAGE_VISUALIZER = 'rwf_homepage_visualizer',
  HOMEPAGE_ANNOUNCE_URL = 'homepage_announce_url',
  HOMEPAGE_ANNOUNCE_LAUNCH = 'homepage_announce_launch',
  HIDE_MDI_TALENTS = 'hide_mdi_talents',
  TWW1_REVIEW = 'tww1_review',
  TWW2_REVIEW = 'tww2_review',
  TWW_REVIEW = 'tww_review',
  HIDE_TWW_REVIEW_DEATHS_HEATMAP_SLIDE = 'hide_tww_review_deaths_heatmap_slide',
  DF3_REVIEW = 'df3_review',
  DF2_REVIEW = 'df2_review',
  DF1_REVIEW = 'df1_review',
  SHADOWLANDS_REVIEW = 'shadowlands_review',
  DRAGONFLIGHT_REVIEW = 'dragonflight_review',
  SL3_REVIEW = 'sl3_review',
  SL2_REVIEW = 'sl2_review',
  SL1_REVIEW = 'sl1_review',
  MDI_LIVE = 'mdi_live',
  RWF_LIVE = 'rwf_live',
  EVENT_PROAM_ACTIVE = 'event_proam_active',

  SETTINGS_GROUP_CUSTOMIZATION = 'settings_group_customization',
  SETTINGS_GROUP_CUSTOMIZATION_PRIVACY = 'settings_group_customization_privacy',
  SETTINGS_GROUP_ROLES = 'settings_group_roles',
  SETTINGS_CHARACTER_CUSTOMIZATION = 'settings_character_customization',
  SETTINGS_SOCIAL = 'settings_social',
  SETTINGS_VANITY_ALIAS = 'settings_vanity_alias',
  SETTINGS_PASSWORD = 'settings_password',
  SETTINGS_USERNAME = 'settings_username',
  SETTINGS_EMAIL = 'settings_email',
  PREMIUM_BGS_ANIMATED = 'premium_bgs_animated',

  SHOW_DUNGEON_ADS = 'show_dungeon_ads',
  ADS_HEADER_SWAP = 'ads_header_swap',
  SHOW_CHARACTER_HISTORY = 'show_character_history',
  SHOW_RUN_EVENT_BREAKDOWN = 'show_run_event_breakdown',
  SHOW_RUN_EVENT_BREAKDOWN_DEV = 'show_run_event_breakdown_dev',
  SNAPSHOTS_CHARACTERS_OPENGRAPH = 'snapshots_characters_opengraph',
  SNAPSHOTS_GUILDS_OPENGRAPH = 'snapshots_guilds_opengraph',
  STREAM_ADMIN = 'stream_admin',
  STREAM_FEATURE_WARCRAFT_THEGREATPUSH = 'stream_feature_warcraft_thegreatpush',
  STREAM_FEATURE_WARCRAFT_AWC = 'stream_feature_warcraft_awc',
  STREAM_FEATURE_WARCRAFT_THELASTSTAND = 'stream_feature_warcraft_thelaststand',
  STREAM_FEATURE_WARCRAFTMDI = 'stream_feature_warcraftmdi',
  STREAM_FEATURE_WARCRAFT_PLUNDERSTORM = 'stream_feature_plunder_creator_royale',
  DASTARDLY_DUOS_MASTHEAD = 'dastardly_duos_masthead',

  MDI_DF2_TGP_WIDGET_LEADERBOARD = 'mdi_df2_tgp_widget_leaderboard',
  EVE_ASCENSION_TGP_WIDGET_LEADERBOARD = 'eve_df2_tgp_widget_leaderboard',

  RWF_VAULT_OF_INCARNATES_MASTHEAD = 'rwf_vault_of_incarnates_masthead',
  RWF_VAULT_OF_INCARNATES_SHOWDOWN = 'rwf_vault_of_incarnates_showdown',
  RWF_VAULT_OF_INCARNATES_RACE_VISUALIZER = 'rwf_vault_of_incarnates_race_visualizer',
  RWF_VAULT_OF_INCARNATES_SHOWDOWN_VERSUS = 'rwf_vault_of_incarnates_showdown_versus',
  RWF_VAULT_OF_INCARNATES_SHOWDOWN_GRAPH = 'rwf_vault_of_incarnates_showdown_graph',
  RWF_VAULT_OF_INCARNATES_SHOWDOWN_TIMELINE = 'rwf_vault_of_incarnates_showdown_timeline',
  ALLOW_2023_PRO_AM = 'allow_2023_pro_am',
  ALLOW_2023_KSB_REGISTRATION = 'allow_2023_ksb_registration',
  ALLOW_2023_UKRAINE_TR_REGISTRATION = 'allow_2023_ukraine_tr_registration',
  RWF_LIVE_COVERAGE_PAGE_KILLS_STRIP = 'rwf_live_coverage_page_kills_strip',
  RWF_ANALYZER_COVERAGE_PAGE = 'rwf_analyzer_coverage_page',

  TEAM_SYSTEM = 'team_system',
  TEAM_SYSTEM_ACTION_RENAME_CHARTER = 'team_system_action_rename_charter',
  TEAM_SYSTEM_ACTION_CREATE_CHARTER = 'team_system_action_create_charter',
  TEAM_SYSTEM_ACTION_ACCEPT_INVITE = 'team_system_action_accept_invite',
  TEAM_SYSTEM_ACTION_REGENERATE_CHARTER_INVITE = 'team_system_action_regenerate_charter_invite',
  TEAM_SYSTEM_ACTION_ADD_CHARACTER = 'team_system_action_add_character',

  THREADS_ADMIN = 'threads_admin',
  THREADS_EDITOR = 'threads_editor',
  UNLOCK_ALL_BGS = 'unlock_all_bgs',
  VANITY_URL = 'vanity_url',

  REPLAY_SET_ADMIN_VIEW = 'replay_set_admin_view',

  BTM_2023_REGISTRATION = 'allow_2023_btm_registration',
  BTM_DF_SEASON_2_REGISTRATION = 'allow_df_season_2_btm_registration',
  CHARITY_PRO_AM_2024_MASTHEAD = 'charity_pro_am_2024_masthead',

  // video features
  MANAGE_KEYSTONE_RUN_VIDEOS = 'manage_keystone_run_videos',
  ADMIN_MANAGE_KEYSTONE_RUN_VIDEOS = 'admin_manage_keystone_run_videos',
  MANAGE_RAID_ENCOUNTER_VIDEOS = 'manage_raid_encounter_videos',
  ADMIN_MANAGE_RAID_ENCOUNTER_VIDEOS = 'admin_manage_raid_encounter_videos',

  KEYSTONE_ROUTE_PRESENTER = 'keystone_route_presenter',

  EVE_ASCENSION_MASTHEAD = 'eve_ascension_masthead',
  EVE_ASCENSION_DF_2_REGISTRATION = 'eve_ascension_df_season_2_registration',
  KSB_2023_FINALS_MASTHEAD = 'ksb_season_3_finals_masthead',

  ABERRUS_MASTHEAD = 'rwf_aberrus_the_shadowed_crucible_masthead',

  AMIRDRASSIL_MASTHEAD = 'rwf_amirdrassil_the_dreams_hope_masthead',

  GUILD_ROSTER_HISTORY_VIEW = 'guild_roster_history_view',
  TEAM_ROSTER_HISTORY_VIEW = 'team_roster_history_view',

  // Oauth2 Auth options
  GOOGLE_OAUTH2_AUTHORIZATION = 'google_oauth2_authorization',

  ALLOW_EVENT_UNLOCKING = 'allow_event_unlocking',
  EVENT_CREATION_SYSTEM = 'event_creation_system',
  EVENT_COLLABORATOR = 'event_collaborator',
  SHOW_SPEC_AGGREGATES_GRAPH = 'show_spec_aggregates_graph',
  HIDE_REGISTRATION_INVITE_CODE = 'hide_registration_invite_code',
  EVENT_REGISTRATION_EXPORT_ACCESS = 'event_registration_export_access',
  EVENT_REGISTRATION_RESTRICTED_EXPORT_ACCESS = 'event_registration_restricted_export_access',
  FILE_MANAGER_VIEW = 'file_manager_view',
  FILE_MANAGER_EDIT = 'file_manager_edit',

  STATISTICS_MIN_GROUP_MEMBERS_COUNT_FILTER = 'statistics_min_group_members_count_filter',
  STATISTICS_SPELLS_SEARCH = 'statistics_spells_search',

  TIMELINE_EVENT_MANAGER_VIEW = 'timeline_event_manager_view',
  TIMELINE_EVENT_MANAGER_EDIT = 'timeline_event_manager_edit',

  REACTIONS_EDIT = 'reactions_edit',
  REACTIONS_THREAD_POST = 'reactions_thread_post',
  BBCODE_FROM_AST = 'bbcode_from_ast',

  // old features
  // SANCTUM_SHOWDOWN_ACTIVE = 'sanctum_showdown_active',
  // SANCTUM_SHOWDOWN_SHOW_PIECES = 'sanctum_showdown_show_pieces',
  // SANCTUM_SHOWDOWN_SHOW_BDGG = 'sanctum_showdown_show_bdgg',
  // RWF_SEPULCHER_MASTHEAD = 'rwf_sepulcher_masthead',
  // RWF_SEPULCHER_SHOWDOWN = 'rwf_sepulcher_showdown',
  // RWF_SEPULCHER_RACE_VISUALIZER = 'rwf_sepulcher_race_visualizer',
  // RWF_SEPULCHER_SHOWDOWN_VERSUS = 'rwf_sepulcher_showdown_versus',
  // RWF_SHOWDOWN_LIVE_VIDEO = 'rwf_showdown_live_video',
  // RWF_SEPULCHER_SHOWDOWN_GRAPH = 'rwf_sepulcher_showdown_graph',
  // STREAM_FEATURE_WARCRAFT_XCC = 'stream_feature_warcraft_xcc',
  // XCC_MASTHEAD_WIDGET_ENABLED = 'xcc_masthead_widget_enabled',

  TWO_FACTOR_AUTH = 'two_factor_auth',

  // maintenance features
  MAINTENANCE = 'maintenance', // global maintenance
  MAINTENANCE_CHARACTER_SCORE = 'maintenance_character_score',
  MAINTENANCE_GUILD_ACHIEVEMENTS = 'maintenance_guild_achievements',
  MAINTENANCE_GUILD_ROSTER = 'maintenance_guild_roster',
  MAINTENANCE_CLIENT = 'maintenance_client',
  MAINTENANCE_CLIENT_AUTH = 'maintenance_client_auth',
  MAINTENANCE_CLIENT_COMBATLOG = 'maintenance_client_combatlog',
  MAINTENANCE_CLIENT_SEGMENT = 'maintenance_client_segment',
  MAINTENANCE_CLIENT_CONFIG_UPLOAD = 'maintenance_client_config_upload',
  MAINTENANCE_CLIENT_LIVE_REDIRECT = 'maintenance_client_live_redirect',
  MAINTENANCE_CLIENT_ADDON_SYNC = 'maintenance_client_addon_sync',
  MAINTENANCE_CLIENT_ADDON_SYNC_COLORS = 'maintenance_client_addon_sync_colors',
  MAINTENANCE_CLIENT_ADDON_SYNC_CHARACTERS = 'maintenance_client_addon_sync_characters',
  MAINTENANCE_CLIENT_ADDON_SYNC_GUILDS = 'maintenance_client_addon_sync_guilds',
  MAINTENANCE_CLIENT_ADDON_SYNC_REPLAYS = 'maintenance_client_addon_sync_replays',
  MAINTENANCE_CLIENT_ADDON_SYNC_RECENT_CHARACTERS = 'maintenance_client_addon_sync_recent_characters',
  MAINTENANCE_CLIENT_STATS = 'maintenance_client_stats',
  MAINTENANCE_CLIENT_DBCACHE_UPLOAD = 'maintenance_client_dbcache_upload',

  MANAGE_API_KEYS = 'manage_api_keys',
  ADMIN_MANAGE_API_KEYS = 'admin_manage_api_keys',
  VIEW_CHARACTER_STATISTICS_PAGE = 'view_character_statistics_page',
  VIEW_GROUP_STATISTICS_PAGE = 'view_group_statistics_page',
  VIEW_USER_STATISTICS_PAGE = 'view_user_statistics_page',

  GUILD_ROSTER_MAINTENANCE = 'guild_roster_maintenance',
  GUILD_ACHIEVEMENTS_MAINTENANCE = 'guild_achievements_maintenance',
  ALLOW_COPY_TALENTS = 'allow_copy_talents',
  ENCOUNTER_BREAKDOWN = 'encounter_breakdown',

  EVENT_BRACKET_EMBED_BUTTON = 'event_bracket_embed_button',
  EVENT_BRACKET_EMBED_PAGE = 'event_bracket_embed_page',

  RAID_COMPARISON_PAGE = 'raid_comparison_page',
  RAID_COMPARISON_BUTTON = 'raid_comparison_button',

  // Content moderation
  CONTENT_MODERATOR = 'content_moderator',
  MODERATION_REPORTING_ENABLED = 'moderation_reporting_enabled'
}

export interface IQualifiedGuildRecruitmentTag {
  tagName: string;
  tagId: number;
  approvedGuildIds: number[];

  // Display properties
  tagClass: string; // CSS class for the tag (e.g., 'rio-rec-tag--lwiw')
  tooltipLocKey: string; // Loc key for tooltip (e.g., 'recruitment:visage.approved-tooltip.description')
  badgeLabelLocKey: string; // Loc key for badge label (e.g., 'recruitment:visage.badge-label')
  badgeImagePath: string; // Asset path (e.g., '/images/visage-verified/badge.png')
  badgeSmallImagePath: string; // Small variant for inline use
  badgeLargeImagePath: string; // Large variant for cards
  badgeAspectRatio: number; // Aspect ratio (e.g., 14/16 = 0.875)
}

// This must be a flat object for the overrides to work properly; otherwise
// when you attempt to override you must set everything inside the overridden
// version
export interface IGameConfig {
  CurrentExpansion: Expansion;
  NextExpansion: Expansion;
  PreviousExpansion: Expansion;
  FirstSupportedExpansion: Expansion;

  CurrentPatch: ExpansionPatch;
  FirstSupportedPatch: ExpansionPatch;
  PublicTestRealmPatch: ExpansionPatch;
  BetaPatch: ExpansionPatch;

  CurrentLevelCap: ExpansionLevelCap;
  PreviousLevelCap: ExpansionLevelCap;
  MinScanLevel: number;

  // path to an asset to use on site background
  SiteBackdropPath?: string;

  Raids: TBaseRaidMetadata[];
  RaidsBySlug: Record<string, TBaseRaidMetadata>;
  RaidDifficulties: Difficulty[];

  MythicPlusSeasons: TSeasonDef[];
  MythicPlusSeasonsSorted: TSeasonDef[];
  MythicPlusSeasonsByPartitionEventId: Map<number, TSeasonDef>;

  getSeasonBySlug: (slug: string) => TSeasonDef;

  Routes: Record<string, string>;

  Expansions: TExpansionInfo[];
  getExpansionById: (id: Expansion) => TExpansionInfo;
  getExpansionBySlug: (slug: string) => TExpansionInfo;

  BaselineFeatures: FeatureFlag[];

  UserConnectionTypes: UserConnectionType[];
  ItemSlotGroups: string[][];

  EventConfig: IGameEventConfig;

  QualifiedGuildRecruitmentTags: IQualifiedGuildRecruitmentTag[];

  getForcePathsFromRoutes?: TForcePathsFromRoutes;
}

export const enum UserConnectionType {
  DOUYU = 'douyu',
  DISCORD = 'discord',
  TWITCH = 'twitch',
  YOUTUBE = 'youtube',
  GOOGLE = 'google'
}
